import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Project } from 'src/app/core/objects/project';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { SharedProjectService } from 'src/app/core/services/shared-project.service';
import { PalletInterlayer, PalletLayer, PalletLayerGroup } from 'src/app/core/types';
import { resolveInterlayerDisplayName } from 'src/app/shared/helpers/pallet-interlayer-name.helper';
import { resolvePalletLayerTypeDisplayName } from 'src/app/shared/helpers/pallet-layer-name.helper';
import { buildPalletLayerGroupEditRequest, buildPalletLayerInterlayerDeleteRequest } from 'src/app/shared/helpers/pallet-layer-add.helper';
import {
  getPalletListDisplayRows,
  PalletListDisplayRow,
} from 'src/app/shared/helpers/pallet-interlayer-display.helper';
import { getPalletLayerGroupsForList } from 'src/app/shared/helpers/pallet-loading-space.helper';
import {
  palletSpaceQuantity,
  replicatedLoadCountLabel,
} from 'src/app/shared/helpers/pallet-workspace-collapse.helper';
import { ColorPipe } from 'src/app/shared/pipes/color.pipe';

@Component({
  selector: 'gl-loads-list-pallet-layer-group',
  templateUrl: './loads-list-pallet-layer-group.component.html',
  styleUrls: ['./loads-list-pallet-layer-group.component.scss'],
  standalone: false,
})
export class LoadsListPalletLayerGroupComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly showInterlayerDeleteButton = true;

  private static openEditor?: LoadsListPalletLayerGroupComponent;
  private readonly pendingLayerDeltas = new Map<number, number>();
  private readonly layerCountTimers = new Map<number, ReturnType<typeof setTimeout>>();
  @Input() public palletGroup: PalletLayerGroup;
  @Input() public workSpaceIndex: number;
  @Input() public cargoSpaceIndex = 0;
  @Input() public stopIndex: number;
  @Input() public lastInList = false;
  @Input() public isSharedProjectLast = true;

  @HostBinding('class.overlay') public isSharedView = false;
  @HostBinding('class.groupEditOpen') public editingGroup = false;

  @ViewChild('groupEdit', { static: false, read: ElementRef })
  private groupEditEl?: ElementRef<HTMLElement>;
  @ViewChild('editTrigger', { static: false })
  public editTriggerEl?: ElementRef<HTMLElement>;

  public isMobile = false;
  public project: Project;
  public displayRows: PalletListDisplayRow[] = [];

  private subscriptions: Subscription[] = [];
  private editPanelResizeObserver?: ResizeObserver;
  private currentWorkSpaceId: number;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private colorPipe: ColorPipe,
    private translate: TranslateService,
    private sharedProjectService: SharedProjectService,
    private router: Router,
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLElement>,
    private notificationsService: NotificationsService,
  ) {
    this.isMobile = this.router.url.includes('mobile');
  }

  ngOnInit(): void {
    this.refreshDisplayRows();
    this.subscriptions.push(
      this.projectService.project.subscribe((project) => {
        this.project = project;
        this.currentWorkSpaceId = project.workSpaces[this.workSpaceIndex]?.id;
        const updated = this.findGroupInProject(project);
        if (updated) {
          this.palletGroup = updated;
          this.refreshDisplayRows();
        }
      }),
    );
    this.subscriptions.push(
      this.sharedProjectService.isViewingShared.subscribe((v) => (this.isSharedView = v)),
    );
    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe((index) => {
        this.currentWorkSpaceId = this.project?.workSpaces?.[index]?.id;
      }),
    );
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.layerCountTimers.forEach((timer) => clearTimeout(timer));
    this.layerCountTimers.clear();
    this.disconnectEditPanelResizeObserver();
    this.subscriptions.forEach((s) => s.unsubscribe());
    if (LoadsListPalletLayerGroupComponent.openEditor === this) {
      LoadsListPalletLayerGroupComponent.openEditor = undefined;
    }
  }

  public getColor(): string {
    return this.colorPipe.transform(this.palletGroup.color);
  }

  private currentPalletQuantity(): number {
    return palletSpaceQuantity(this.project?.workSpaces?.[this.workSpaceIndex]);
  }

  private replicatedQuantityLabel(onPallet: number): string {
    return replicatedLoadCountLabel(onPallet, this.currentPalletQuantity()) ?? `${onPallet}`;
  }

  public totalQuantity(): number {
    return this.palletGroup?.quantity ?? 0;
  }

  public totalQuantityLabel(): string {
    return this.replicatedQuantityLabel(this.totalQuantity());
  }

  public showReplicatedLoadHint(): boolean {
    return this.currentPalletQuantity() > 1;
  }

  public layerTypeName(layer: PalletLayer): string {
    return resolvePalletLayerTypeDisplayName(
      layer,
      this.palletGroup?.layers ?? [],
      (key, params) => String(this.translate.instant(key, params)),
    );
  }

  public layerHeight(layer: PalletLayer): number {
    return Math.round(layer.dimensions?.height ?? this.palletGroup.dimensions?.height ?? 0);
  }

  public layerQuantity(layer: PalletLayer): number {
    return layer.quantity ?? 0;
  }

  public layerQuantityLabel(layer: PalletLayer): string {
    return this.replicatedQuantityLabel(this.layerQuantity(layer));
  }

  public layerTypeCount(layer: PalletLayer): number {
    const pending = layer.id == null ? 0 : (this.pendingLayerDeltas.get(layer.id) ?? 0);
    return Math.max(0, (layer.layersLoadedQuantity ?? 0) + pending);
  }

  public interlayerName(interlayer: PalletInterlayer): string {
    return resolveInterlayerDisplayName(
      interlayer.name,
      (key, params) => String(this.translate.instant(key, params)),
      { emptyFallbackKey: 'interlayers_tab' },
    );
  }

  public interlayerThickness(interlayer: PalletInterlayer): number {
    return Math.round(interlayer.dimensions?.height ?? 0);
  }

  public toggleGroupEdit(): void {
    if (this.isSharedView || this.isMobile) {
      return;
    }
    if (this.editingGroup) {
      this.closeGroupEdit();
      return;
    }
    if (
      LoadsListPalletLayerGroupComponent.openEditor &&
      LoadsListPalletLayerGroupComponent.openEditor !== this
    ) {
      LoadsListPalletLayerGroupComponent.openEditor.closeGroupEdit();
    }
    LoadsListPalletLayerGroupComponent.openEditor = this;
    this.editingGroup = true;
    window.setTimeout(() => {
      this.positionGroupEditPanel();
      this.observeEditPanelResize();
    }, 0);
  }

  public onGroupEditLayoutChange(): void {
    if (this.editingGroup) {
      window.setTimeout(() => this.positionGroupEditPanel(), 0);
    }
  }

  public closeGroupEdit(): void {
    this.disconnectEditPanelResizeObserver();
    this.editingGroup = false;
    if (LoadsListPalletLayerGroupComponent.openEditor === this) {
      LoadsListPalletLayerGroupComponent.openEditor = undefined;
    }
  }

  public isLayerRow(row: PalletListDisplayRow): row is Extract<PalletListDisplayRow, { kind: 'layer' }> {
    return row.kind === 'layer';
  }

  public isInterlayerRow(
    row: PalletListDisplayRow,
  ): row is Extract<PalletListDisplayRow, { kind: 'interlayer' }> {
    return row.kind === 'interlayer';
  }

  public decrementLayerCount(layer: PalletLayer): void {
    this.queueLayerCount(layer, -1);
  }

  public incrementLayerCount(layer: PalletLayer): void {
    this.queueLayerCount(layer, 1);
  }

  public deleteInterlayer(event?: Event): void {
    event?.stopPropagation();
    if (this.isSharedView || this.isMobile || !this.palletGroup?.interlayer) {
      return;
    }

    const workSpace = this.project.workSpaces[this.workSpaceIndex];
    const body = buildPalletLayerInterlayerDeleteRequest({
      currentWorkSpaceId: this.currentWorkSpaceId,
      workSpaceId: workSpace.id,
      stopId:
        this.stopIndex !== undefined && this.stopIndex !== null && this.stopIndex >= 0
          ? workSpace.stops?.[this.stopIndex]?.id
          : undefined,
      layerGroupId: this.palletGroup.id,
    });

    this.projectApiService.deletePalletLayerInterlayer(body).subscribe((result) => {
      this.notificationsService.generateCOGNotification(result);
    });
  }

  private refreshDisplayRows(): void {
    this.displayRows = getPalletListDisplayRows(this.palletGroup);
  }

  private queueLayerCount(layer: PalletLayer, delta: number): void {
    if (this.isSharedView || this.isMobile || layer.id == null) {
      return;
    }
    if (delta < 0 && this.layerTypeCount(layer) <= 0) {
      return;
    }
    const nextPending = (this.pendingLayerDeltas.get(layer.id) ?? 0) + delta;
    if (nextPending === 0) {
      this.pendingLayerDeltas.delete(layer.id);
    } else {
      this.pendingLayerDeltas.set(layer.id, nextPending);
    }
    const pendingTimer = this.layerCountTimers.get(layer.id);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
    }
    this.layerCountTimers.set(
      layer.id,
      setTimeout(() => this.flushLayerCount(layer), 600),
    );
  }

  private flushLayerCount(layer: PalletLayer): void {
    if (layer.id == null) {
      return;
    }
    this.layerCountTimers.delete(layer.id);
    const delta = this.pendingLayerDeltas.get(layer.id);
    this.pendingLayerDeltas.delete(layer.id);
    if (!delta) {
      return;
    }
    layer.layersLoadedQuantity = Math.max(0, (layer.layersLoadedQuantity ?? 0) + delta);
    this.changeLayerCount(layer, delta);
  }

  private changeLayerCount(layer: PalletLayer, delta: number): void {
    const workSpace = this.project.workSpaces[this.workSpaceIndex];
    const body = buildPalletLayerGroupEditRequest({
      currentWorkSpaceId: this.currentWorkSpaceId,
      workSpaceId: workSpace.id,
      stopId:
        this.stopIndex !== undefined && this.stopIndex !== null && this.stopIndex >= 0
          ? workSpace.stops?.[this.stopIndex]?.id
          : undefined,
      layerGroupId: this.palletGroup.id,
      layers: [{ layerId: layer.id, quantityDifference: delta }],
    });

    this.projectApiService.editPalletLayerGroup(body).subscribe((result) => {
      this.notificationsService.generateCOGNotification(result);
    });
  }

  private positionGroupEditPanel(): void {
    if (!this.groupEditEl) {
      return;
    }
    const editWindowChildRect = this.groupEditEl.nativeElement.children[0]?.getBoundingClientRect();
    const panelHeight = editWindowChildRect?.height ?? 0;
    const top = this.calculateGroupEditTopPosition(panelHeight);
    this.renderer.setStyle(this.groupEditEl.nativeElement, 'left', '450px');
    this.renderer.setStyle(this.groupEditEl.nativeElement, 'top', `${top}px`);
    this.renderer.setStyle(this.groupEditEl.nativeElement, 'visibility', 'visible');
  }

  private calculateGroupEditTopPosition(panelHeight: number): number {
    const listElementRect = this.elRef.nativeElement.getBoundingClientRect();
    const NAVBAR_HEIGHT = 50;
    let top = listElementRect.top - NAVBAR_HEIGHT;
    const isBottomVisible = top + panelHeight + NAVBAR_HEIGHT < window.innerHeight;
    if (!isBottomVisible) {
      top = top - (top + panelHeight + 70 - window.innerHeight);
    }
    return Math.max(0, top);
  }

  private observeEditPanelResize(): void {
    this.disconnectEditPanelResizeObserver();
    if (!this.groupEditEl?.nativeElement || typeof ResizeObserver === 'undefined') {
      return;
    }
    const target = this.groupEditEl.nativeElement.children[0] ?? this.groupEditEl.nativeElement;
    this.editPanelResizeObserver = new ResizeObserver(() => {
      if (this.editingGroup) {
        this.positionGroupEditPanel();
      }
    });
    this.editPanelResizeObserver.observe(target);
  }

  private disconnectEditPanelResizeObserver(): void {
    this.editPanelResizeObserver?.disconnect();
    this.editPanelResizeObserver = undefined;
  }

  private findGroupInProject(project = this.project): PalletLayerGroup | undefined {
    const groups = getPalletLayerGroupsForList(
      project,
      this.workSpaceIndex,
      this.cargoSpaceIndex,
      this.stopIndex,
    );
    return groups.find((g) => g.id === this.palletGroup?.id);
  }
}
